import { useEffect } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  billPreviewsState,
  failedPaymentBillPkState,
} from "../../recoil/atoms";
import {
  GetUpfrontPaymentPlan,
  UpfrontPaymentPlan,
} from "../UpfrontPayment/Services/UpfrontPaymentService";

/**
 * Pure helper function to check if a payment plan has any failed or retryable installments
 * @param paymentPlan - Array of payment plan installments
 * @returns true if any installment has a failed or retryable stripe_sync_status
 */
export const hasFailedOrRetryableInstallment = (
  paymentPlan: UpfrontPaymentPlan[],
): boolean =>
  paymentPlan.some(
    (invoice) =>
      invoice.stripe_sync_status === "failed" ||
      invoice.stripe_sync_status === "retryable",
  );

/**
 * Component that checks for failed payment installments across all bills.
 * This component stays mounted in AppContainer to avoid re-running checks
 * when navigating between pages.
 */
export const FailedPaymentChecker = () => {
  const billPreviews = useRecoilValue(billPreviewsState);
  const [, setFailedPaymentBillPk] = useRecoilState(failedPaymentBillPkState);

  useEffect(() => {
    // Skip if no bills to check (e.g., user not logged in or no bills loaded)
    if (billPreviews.length === 0) {
      setFailedPaymentBillPk("");
      return;
    }

    const checkForFailedPayments = async () => {
      // Filter bills with active payment plans (in_progress status)
      const inProgressBills = billPreviews.filter(
        (bill) => bill.portal_bill_status === "in_progress",
      );

      const paymentPlanPromises = inProgressBills.map(async (bill) => {
        try {
          const response = await GetUpfrontPaymentPlan(bill.pk);
          if (!response.ok) {
            console.error(
              `Failed to fetch payment plan for bill ${bill.pk}: ${response.status}`,
            );
            return { billPk: bill.pk, paymentPlan: null, error: true };
          }

          const paymentPlan: UpfrontPaymentPlan[] = await response.json();
          return { billPk: bill.pk, paymentPlan, error: false };
        } catch (error) {
          console.error(
            `Error checking failed installments for bill ${bill.pk}:`,
            error,
          );
          return { billPk: bill.pk, paymentPlan: null, error: true };
        }
      });

      const results = await Promise.allSettled(paymentPlanPromises);

      for (const result of results) {
        if (result.status === "fulfilled" && !result.value.error) {
          const { billPk, paymentPlan } = result.value;
          if (paymentPlan && hasFailedOrRetryableInstallment(paymentPlan)) {
            setFailedPaymentBillPk(billPk);
            return; // Stop checking after finding the first failed bill
          }
        }
      }

      // No failed bills found, clear the state
      setFailedPaymentBillPk("");
    };

    checkForFailedPayments();
  }, [billPreviews, setFailedPaymentBillPk]);

  // This component renders nothing
  return null;
};
